import { produce } from "immer";
import { useMemo, useState } from "react";
import type { MyBusinessLocationCategoryDto } from "shared/src/types/google/business";
import { useEventCallback } from "usehooks-ts";
import { Dialog, useDialog } from "../common/dialog/Dialog";
import type { UseFormReturn } from "../common/form/useForm";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { TabPill } from "../common/TabPill";
import { Button } from "../components/Button";
import type { FormMyBusinessLocationFields } from "./FormBusinessLocationFields";

interface DialogAddServiceProps {
	selectedCategories: MyBusinessLocationCategoryDto[];
	form: UseFormReturn<FormMyBusinessLocationFields>;
}

export function DialogAddService(props: DialogAddServiceProps) {
	const { selectedCategories, form } = props;
	const dialog = useDialog();
	const services = useMemo(
		() => selectedCategories.flatMap((cat) => cat.serviceTypes ?? []),
		[selectedCategories],
	);
	const [tab, setTab] = useState<"predefined" | "freetext">(() =>
		selectedCategories.some((c) => (c.serviceTypes?.length ?? 0) > 0)
			? "predefined"
			: "freetext",
	);
	const [freeFormText, setFreeFormText] = useState("");
	const [freeFormCategory, setFreeFormCategory] = useState(
		() => selectedCategories[0]?.name ?? "",
	);
	const [structuredService, setStructuredService] = useState("");

	const categoryOptions = useMemo(() => {
		return selectedCategories
			.filter((c) => !!c.name)
			.map((category) => ({
				value: category.name as string,
				label:
					category.displayName?.trim() ||
					(category.name as string)
						.replace(/^categories\//i, "")
						.replace(/^gcid[_:]/i, "")
						.replace(/_/g, " "),
			}));
	}, [selectedCategories]);

	const handleConfirm = useEventCallback(() => {
		if (tab === "predefined") {
			const newItem = services.find(
				(x) => x.serviceTypeId === structuredService,
			);

			if (!newItem) {
				return;
			}

			const newValue = produce(
				form.values.structuredServices ?? [],
				(draft) => {
					draft.push(structuredService);
				},
			);

			form.setFieldValue("structuredServices", newValue);
		} else {
			if (!freeFormCategory || !freeFormText) {
				return;
			}

			const newValue = produce(form.values.freeformServices ?? [], (draft) => {
				draft.push({
					category: freeFormCategory,
					name: freeFormText,
				});
			});

			form.setFieldValue("freeformServices", newValue);
		}

		dialog.close();
	});

	return (
		<Dialog>
			<Dialog.Header title="Add a service" />
			<Dialog.Body>
				<TabPill.List>
					<TabPill
						onClick={() => setTab("predefined")}
						active={tab === "predefined"}
					>
						Pre-defined
					</TabPill>
					<TabPill
						onClick={() => setTab("freetext")}
						active={tab === "freetext"}
					>
						Free-text
					</TabPill>
				</TabPill.List>
				<div className="py-3">
					{tab === "predefined" && (
						<ExtraForm.Group label="Service name">
							{services.length === 0 ? (
								<div style={{ fontSize: 13, color: "#6c757d" }}>
									Google has no pre-defined services for your selected
									categories (e.g. Manufacturer). Switch to{" "}
									<strong>Free-text</strong> to add a custom service.
								</div>
							) : (
								<ExtraForm.Select
									onChange={(e) => setStructuredService(e.target.value)}
								>
									<option value="">Select Service</option>
									{services.map((service) => (
										<option
											key={service.serviceTypeId}
											disabled={
												!!form.values.structuredServices?.find(
													(srv) => srv === (service.serviceTypeId ?? ""),
												)
											}
											value={service.serviceTypeId as string}
										>
											{service.displayName}
										</option>
									))}
								</ExtraForm.Select>
							)}
						</ExtraForm.Group>
					)}
					{tab === "freetext" && (
						<>
							<ExtraForm.Group label="Service name">
								<ExtraForm.Control
									value={freeFormText}
									placeholder="Service name"
									type="text"
									onChange={(e) => setFreeFormText(e.target.value)}
								/>
							</ExtraForm.Group>
							<ExtraForm.Group label="Category">
								{categoryOptions.length === 0 ? (
									<div style={{ fontSize: 13, color: "#6c757d" }}>
										Select a primary category on the form first, then add a
										service.
									</div>
								) : (
									<ExtraForm.Select
										onChange={(e) => setFreeFormCategory(e.target.value)}
										value={freeFormCategory}
									>
										<option value="">Select Category</option>
										{categoryOptions.map((category) => (
											<option key={category.value} value={category.value}>
												{category.label}
											</option>
										))}
									</ExtraForm.Select>
								)}
							</ExtraForm.Group>
						</>
					)}
				</div>
			</Dialog.Body>
			<Dialog.Footer className="d-flex justify-content-end">
				<Button size="sm" onClick={handleConfirm}>
					Add
				</Button>
			</Dialog.Footer>
		</Dialog>
	);
}
